import { PayeeResolution } from './PayeeResolution';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PaymentQueueBucket, PaymentQueueRow } from '../../api/types';
import { OrderLabel } from './OrderLabel';
import { SlaChip } from './SlaChip';
import { formatMoney, parsePaise } from './allocation';
import { formatIstDateTime } from './paymentDates';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
import { buttonClassName } from '../../components/ui/buttonClassName';

const PAYEE_REASONS: Record<string, string> = {
  NO_BANK: 'No bank details',
  KYC_PENDING: 'KYC pending',
  KYC_REJECTED: 'KYC rejected',
  NO_FLEET_MANAGER: 'No partner',
};
export function OrderQueueTable({
  bucket,
  rows,
  selected,
  onSelect,
  onPayout,
  onReceipt,
  onInvoice,
  onPayment,
  onAllocateCredit,
}: {
  bucket: PaymentQueueBucket;
  partnerAdvancePct?: number;
  rows: PaymentQueueRow[];
  selected: PaymentQueueRow[];
  onSelect: (row: PaymentQueueRow, checked: boolean) => void;
  onPayout: (rows: PaymentQueueRow[]) => void;
  onReceipt: (row: PaymentQueueRow) => void;
  onInvoice: (id: number) => void;
  onPayment: (id: number) => void;
  onAllocateCredit?: (id: number) => void;
}) {
  const { t } = useTranslation();
  const payout = bucket === 'ADVANCE_DUE' || bucket === 'BALANCE_DUE';
  const invoice = bucket === 'TO_INVOICE',
    collection = bucket === 'COLLECTIONS',
    credit = bucket === 'UNALLOCATED_RECEIPTS',
    blocked = bucket === 'PAYEE_NOT_READY';
  const labels = [
    ...(payout ? ['Select'] : []),
    credit ? 'Receipt' : 'Order',
    invoice || collection || credit ? 'Shipper' : 'Partner',
    'Owner',
    bucket === 'ADVANCE_DUE'
      ? 'Advance due'
      : bucket === 'BALANCE_DUE'
        ? 'Balance due'
        : credit
          ? 'Unallocated credit'
          : collection
            ? 'Outstanding'
            : 'Amount',
    ...(blocked ? ['Reason'] : []),
    ...(!credit ? ['Progress'] : []),
    'Due by (IST)',
    'SLA',
    'Action',
  ];
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {labels.map((label) => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const disabled =
              selected.length > 0 && selected[0].fleetManagerUserId !== row.fleetManagerUserId;
            const paid = parsePaise(row.progressPaid),
              total = parsePaise(row.progressTotal);
            const pct = total > 0n ? Number((paid * 100n) / total) : 0;
            return (
              <tr key={`${row.shipmentId}-${row.paymentId}-${index}`}>
                {payout && (
                  <td>
                    <span title={disabled ? 'Select orders from one partner only' : undefined}>
                      <input
                        type="checkbox"
                        aria-label={`Select order #${row.shipmentId}`}
                        disabled={disabled || row.fleetManagerUserId == null}
                        checked={selected.some((item) => item.shipmentId === row.shipmentId)}
                        onChange={(event) => onSelect(row, event.target.checked)}
                      />
                    </span>
                  </td>
                )}
                <td>
                  {row.shipmentId != null ? (
                    <OrderLabel
                      shipmentId={row.shipmentId}
                      {...{
                        orderNumber: row.orderNumber,
                        route: row.route,
                        vehicleNumber: row.vehicleNumber,
                      }}
                    />
                  ) : row.paymentId != null ? (
                    <button className={styles.orderLink} onClick={() => onPayment(row.paymentId!)}>
                      #{row.paymentId}
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {invoice || collection || credit
                    ? (row.shipperName ?? row.counterpartyName ?? '—')
                    : (row.partnerName ?? '—')}
                </td>
                <td>
                  <span className={styles.owner}>
                    {row.ownerName ?? (credit ? 'Not applicable' : 'Unassigned')}
                  </span>
                </td>
                <td>
                  <strong>{formatMoney(parsePaise(row.amount))}</strong>
                  {bucket === 'ADVANCE_DUE' && (
                    <div className={styles.subtle}>
                      {t('payments.orderQueueTable.advanceCap', { amount: formatMoney(parsePaise(row.advanceCap ?? row.progressTotal)) })}
                    </div>
                  )}
                </td>
                {blocked && (
                  <td>
                    {PAYEE_REASONS[row.payeeBlockReason ?? ''] ?? 'Payee details unavailable'}
                  </td>
                )}
                {!credit && (
                  <td>
                    <progress
                      className={styles.progress}
                      max={100}
                      value={Math.min(100, Math.max(0, pct))}
                      aria-label={`Progress for order #${row.shipmentId}`}
                    />
                    <div className={styles.subtle}>
                      {formatMoney(paid)} / {formatMoney(total)} · {pct}%
                    </div>
                  </td>
                )}
                <td>{row.dueAt ? formatIstDateTime(row.dueAt) : '—'}</td>
                <td>
                  <SlaChip state={row.slaState} dueAt={row.dueAt} />
                </td>
                <td>
                  {payout ? (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={row.fleetManagerUserId == null}
                      onClick={() => onPayout([row])}
                    >
                      {t('payments.orderQueueTable.requestPayout')}
                    </Button>
                  ) : invoice && row.shipmentId != null ? (
                    <div className={styles.queueActions}>
                      {row.invoiceNeeded !== false && !row.issuedInvoiceId && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => onInvoice(row.shipmentId!)}
                        >
                          {t('payments.orderQueueTable.generateInvoice')}
                        </Button>
                      )}
                      {row.issuedInvoiceId != null && (
                        <a
                          className={buttonClassName('secondary', 'sm')}
                          href={`/invoices/${row.issuedInvoiceId}/print`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={t('payments.orderQueueTable.viewInvoiceNewTab')}
                        >
                          {t('payments.orderQueueTable.viewInvoice')}
                        </a>
                      )}
                      {row.partnerBillNeeded && (
                        <Link
                          className={buttonClassName('secondary', 'sm')}
                          to={`/shipments/${row.shipmentId}?tab=payments&partnerBill=1`}
                        >
                          {t('payments.orderQueueTable.partnerBillDetails')}
                        </Link>
                      )}
                      <small>
                        {row.invoiceNeeded === false ? 'Invoice complete' : 'Invoice needed'}
                        {row.partnerBillNeeded ? ' · Partner bill missing' : ''}
                      </small>
                    </div>
                  ) : credit ? (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={
                        row.paymentId == null || parsePaise(row.amount) <= 0n || !onAllocateCredit
                      }
                      onClick={() => onAllocateCredit?.(row.paymentId!)}
                    >
                      {t('payments.orderQueueTable.allocateCredit')}
                    </Button>
                  ) : collection ? (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={row.shipperUserId == null}
                      onClick={() => onReceipt(row)}
                    >
                      {t('payments.orderQueueTable.recordReceipt')}
                    </Button>
                  ) : blocked ? (
                    <PayeeResolution
                      partnerId={row.fleetManagerUserId}
                      reason={row.payeeBlockReason}
                    />
                  ) : row.shipmentId != null ? (
                    <OrderLabel shipmentId={row.shipmentId} />
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
